import { createBrowserRouter, Outlet } from 'react-router-dom';

import { AuthProvider } from '@/auth/AuthProvider';
import { RequireAuth } from '@/auth/RequireAuth';
import { AppShell } from '@/shell/AppShell';

import { CaseWorkspacePage } from './cases/CaseWorkspacePage';
import { CaseListPage } from './cases/CaseListPage';
// TEMPORARY (agreements/calendar/reports agent) — removed again after verification.
import { AgreementDetailPage } from './agreements/AgreementDetailPage';
import { AgreementListPage } from './agreements/AgreementListPage';
import { CalendarPage } from './calendar/CalendarPage';
import { CriticalDatesPage } from './calendar/CriticalDatesPage';
import { ReportsPage } from './reports/ReportsPage';
import { DashboardPage } from './DashboardPage';
import { DocumentListPage } from './documents/DocumentListPage';
import { DocumentReviewPage } from './documents/DocumentReviewPage';
import { InvoiceDetailPage } from './finance/InvoiceDetailPage';
import { InvoiceListPage } from './finance/InvoiceListPage';
import { PaymentQueuePage } from './finance/PaymentQueuePage';
import { LeadDetailPage } from './leads/LeadDetailPage';
import { LeadListPage } from './leads/LeadListPage';
import { NewCasePage } from './cases/NewCasePage';
import { NewLeadPage } from './leads/NewLeadPage';
import { AcceptInvitationPage } from './AcceptInvitationPage';
import { DemoSignupPage } from './landing/DemoSignupPage';
import { AppDownloadPage } from './landing/AppDownloadPage';
import { LandingPage } from './landing/LandingPage';
import { LoginPage } from './LoginPage';
import { NotFoundPage } from './NotFoundPage';
import { TaskDetailPage } from './TaskDetailPage';
import { TaskListPage } from './TaskListPage';
// TEMPORARY (clients/settings agent) — remove with the routes below after verification.
import { ClientDetailPage } from './clients/ClientDetailPage';
import { ClientListPage } from './clients/ClientListPage';
import { NewClientPage } from './clients/NewClientPage';
import { AgreementTemplatesPage } from './settings/AgreementTemplatesPage';
import { ClientProgressPage } from './settings/ClientProgressPage';
import { CustomFieldsPage } from './settings/CustomFieldsPage';
import { DepartmentsPage } from './settings/DepartmentsPage';
import { ConversationsPage } from './ConversationsPage';
import { FirmEmailPage } from './settings/FirmEmailPage';
import { MatterTypesPage } from './settings/MatterTypesPage';
import { OfficesPage } from './settings/OfficesPage';
import { OrganizationPage } from './settings/OrganizationPage';
import { RolesPage } from './settings/RolesPage';
import { SettingsIndexPage } from './settings/SettingsIndexPage';
import { SettingsLayout } from './settings/SettingsLayout';
import { UsersPage } from './settings/UsersPage';

/**
 * `AuthProvider` sits inside the router rather than outside it, so it can navigate
 * on session expiry. Everything below `RequireAuth` is behind a session.
 *
 * `handle.crumb` feeds `Breadcrumbs`. Give every route one — a screen without a crumb
 * simply disappears from the trail, which reads as a bug.
 */
function AuthLayout() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

export const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      // What the product is, for anybody. A signed-out visitor also gets it at `/`
      // (RequireAuth), so the bare domain is the front door rather than a sign-in form.
      { path: '/welcome', element: <LandingPage /> },
      // Self-serve demo sign-up. Public: the person has no account yet.
      { path: '/demo', element: <DemoSignupPage /> },
      // Where clients install the Android app. Public: they have no staff account.
      { path: '/app', element: <AppDownloadPage /> },
      // Outside RequireAuth: the whole point is that the person has no session yet.
      // The path matches the link in the invitation email; changing one without the
      // other silently breaks every invitation already in somebody's inbox.
      { path: '/auth/accept-invitation', element: <AcceptInvitationPage /> },
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AppShell />,
            children: [
              { index: true, element: <DashboardPage />, handle: { crumb: 'My day' } },

              {
                path: 'cases',
                handle: { crumb: 'Matters' },
                children: [
                  { index: true, element: <CaseListPage /> },
                  // Before the parameterised route. Otherwise "new" is read as an
                  // identifier and the workspace tries to open a matter called new,
                  // which is what the button did before this existed.
                  { path: 'new', element: <NewCasePage />, handle: { crumb: 'New' } },
                  { path: ':caseId', element: <CaseWorkspacePage /> },
                ],
              },

              {
                path: 'leads',
                handle: { crumb: 'Leads' },
                children: [
                  { index: true, element: <LeadListPage /> },
                  { path: 'new', element: <NewLeadPage />, handle: { crumb: 'New' } },
                  { path: ':leadId', element: <LeadDetailPage /> },
                ],
              },

              {
                path: 'tasks',
                handle: { crumb: 'Tasks' },
                children: [
                  { index: true, element: <TaskListPage /> },
                  { path: ':taskId', element: <TaskDetailPage /> },
                ],
              },
              {
                path: 'documents',
                handle: { crumb: 'Documents' },
                children: [
                  { index: true, element: <DocumentListPage /> },
                  {
                    path: 'review',
                    element: <DocumentReviewPage />,
                    handle: { crumb: 'Review queue' },
                  },
                ],
              },
              {
                path: 'invoices',
                handle: { crumb: 'Invoices' },
                children: [
                  { index: true, element: <InvoiceListPage /> },
                  { path: ':invoiceId', element: <InvoiceDetailPage /> },
                ],
              },
              {
                path: 'payments',
                element: <PaymentQueuePage />,
                handle: { crumb: 'Payments' },
              },

              {
                path: 'clients',
                handle: { crumb: 'Clients' },
                children: [
                  { index: true, element: <ClientListPage /> },
                  { path: 'new', element: <NewClientPage />, handle: { crumb: 'New' } },
                  { path: ':clientId', element: <ClientDetailPage /> },
                ],
              },
              {
                path: 'agreements',
                handle: { crumb: 'Agreements' },
                children: [
                  { index: true, element: <AgreementListPage /> },
                  { path: ':agreementId', element: <AgreementDetailPage /> },
                ],
              },
              {
                path: 'conversations',
                element: <ConversationsPage />,
                handle: { crumb: 'Conversations' },
              },
              {
                path: 'critical-dates',
                element: <CriticalDatesPage />,
                handle: { crumb: 'Critical dates' },
              },
              {
                path: 'calendar',
                element: <CalendarPage />,
                handle: { crumb: 'Calendar' },
              },
              {
                path: 'reports',
                element: <ReportsPage />,
                handle: { crumb: 'Reports' },
              },
              {
                path: 'settings',
                element: <SettingsLayout />,
                handle: { crumb: 'Settings' },
                children: [
                  { index: true, element: <SettingsIndexPage /> },
                  { path: 'organization', element: <OrganizationPage />, handle: { crumb: 'Firm profile' } },
                  { path: 'users', element: <UsersPage />, handle: { crumb: 'Users' } },
                  { path: 'roles', element: <RolesPage />, handle: { crumb: 'Roles' } },
                  { path: 'departments', element: <DepartmentsPage />, handle: { crumb: 'Departments' } },
                  { path: 'offices', element: <OfficesPage />, handle: { crumb: 'Offices' } },
                  {
                    path: 'email',
                    element: <FirmEmailPage />,
                    handle: { crumb: 'Firm email' },
                  },
                  {
                    // The path the confirmation link points at. Changing it without
                    // changing `_send_confirmation` in the backend silently breaks
                    // every link already sitting in an inbox, and those live a week.
                    path: 'email/confirm',
                    element: <FirmEmailPage />,
                    handle: { crumb: 'Firm email' },
                  },
                  {
                    path: 'matter-types',
                    element: <MatterTypesPage />,
                    handle: { crumb: 'Matter types' },
                  },
                  {
                    path: 'client-progress',
                    element: <ClientProgressPage />,
                    handle: { crumb: 'Client progress' },
                  },
                  {
                    path: 'custom-fields',
                    element: <CustomFieldsPage />,
                    handle: { crumb: 'Custom fields' },
                  },
                  {
                    path: 'agreement-templates',
                    element: <AgreementTemplatesPage />,
                    handle: { crumb: 'Agreement templates' },
                  },
                ],
              },

              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
]);
